import Quill from 'quill';
import _ from 'lodash';
import { Message, Notification } from 'element-ui';

import i18n from '@/i18n';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';
import TextGenerationApi from '@/store/api/textGeneration';

const Module = Quill.import('core/module');

const FEATURE_KEY = 'ai-content';

// The review reminder that is open, so a second text adds no second one: shared by every editor on the page.
let openDisclaimer = null;

/**
 * A generated text may state wrong facts: a reminder at the top to review it, open until it is closed.
 */
function showDisclaimer() {
    if (openDisclaimer) {
        return;
    }

    openDisclaimer = Message.warning({
        dangerouslyUseHTMLString: true,
        message: `<h3>${i18n.t('Please check the text')}</h3><p>${i18n.t('AI can get facts wrong. Read the text carefully before you publish it.')}</p>`,
        customClass: 'ai-content-disclaimer',
        iconClass: 'fas el-icon-fa-exclamation-triangle',
        showClose: true,
        duration: 0,
        onClose: () => {
            openDisclaimer = null;
        },
    });
}

/**
 * Toolbar control -> EX_AI_TextFormat. The ONE place a Quill control is
 * translated into a formatting capability; a control that is not listed
 * (align, color, placeholder, …) is never offered to the model. The link
 * control is mapped through LINK_FORMATS.
 * Keys are `format` or `format:value` as Quill's toolbar reports them.
 */
const TEXT_FORMATS = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strike: 'strike',
    'header:1': 'heading_1',
    'header:2': 'heading_2',
    'header:3': 'heading_3',
    'header:4': 'heading_4',
    'header:5': 'heading_5',
    'header:6': 'heading_6',
    'list:ordered': 'ordered_list',
    'list:bullet': 'bullet_list',
    blockquote: 'blockquote',
    'code-block': 'code_block',
    code: 'inline_code',
};

/**
 * The kinds the editor's link tool offers (quill/toolbar/link, `types`) ->
 * EX_AI_TextFormat. Media links are not offered: the model knows no files.
 */
const LINK_FORMATS = {
    internal: 'link_internal',
    posts: 'link_internal',
    external: 'link_external',
    email: 'link_email',
    phone: 'link_phone',
};

/**
 * AI text generation (docs/features/AI_CONTENT.md): a prompt panel that inserts
 * a text formatted for exactly what THIS editor's toolbar offers. Shown only
 * when the CMS booked `ai-content`.
 */
class TextGenerationModule extends Module {
    constructor(quill, options) {
        super(quill, options);

        this.quill = quill;
        this.panel = null;
        this.range = null;
        this.generating = false;

        this.toolbar = quill.getModule('toolbar');
        if (this.toolbar) {
            this.toolbar.addHandler('text-generation', () => this.toggle());

            // The toolbar attaches its buttons before this module exists and skips
            // one that is neither a registered format nor handled yet, so the
            // button gets its click listener only here, now that the handler is.
            const button = this.toolbar.container.querySelector('.ql-text-generation');
            if (button) {
                this.toolbar.attach(button);
            }
        }

        this.boundOnMouseDown = this.onMouseDown.bind(this);
        document.addEventListener('mousedown', this.boundOnMouseDown);

        this.updateButtonVisibility();
    }

    isEnabled() {
        return useSetupStore(pinia).activeExtensions.includes(FEATURE_KEY);
    }

    updateButtonVisibility() {
        const button = this.toolbar && this.toolbar.container.querySelector('.ql-text-generation');
        if (!button) return;

        const group = button.closest('.ql-formats') || button;
        group.style.display = this.isEnabled() ? '' : 'none';
    }

    /**
     * What this editor can display, read from the rendered toolbar so a section
     * toolbar with optional rows is described exactly as the user sees it.
     */
    formats() {
        const controls = this.toolbar ? this.toolbar.controls : [];

        return _.uniq(
            _.flatMap(controls, ([format, input]) => {
                if (format === 'link') {
                    const linkTool = this.quill.getModule('link');
                    return _.map((linkTool && linkTool.types) || [], (type) => LINK_FORMATS[type]);
                }

                const values =
                    input.tagName === 'SELECT' ? _.map(input.options, (option) => option.value) : [input.value];

                return values.map((value) => TEXT_FORMATS[value ? `${format}:${value}` : format]);
            }).filter(Boolean)
        );
    }

    /**
     * EX_AI_TextContextInput: the host names the record the editor belongs to
     * (option `context`, see withTextGenerationContext), the module adds the
     * field itself. The server builds everything else.
     */
    context() {
        const reference = (typeof this.options.context === 'function' && this.options.context()) || {};
        const id = parseInt(reference.id, 10);
        const namesRecord = !!reference.type && !isNaN(id);
        const selectedText = this.range && this.range.length
            ? _.trim(this.quill.getText(this.range.index, this.range.length))
            : '';

        return _.omitBy({
            type: namesRecord ? reference.type : null,
            id: namesRecord ? id : null,
            title: _.trim(reference.title) || null,
            current_text: _.trim(this.quill.getText()) || null,
            selected_text: selectedText || null,
        }, _.isNull);
    }

    toggle() {
        if (this.panel) {
            this.close();
            return;
        }

        this.quill.focus();
        this.range = this.quill.getSelection() || { index: this.quill.getLength() - 1, length: 0 };
        this.open();
    }

    open() {
        this.panel = document.createElement('div');
        this.panel.id = 'text-generation-panel';

        this.promptField = document.createElement('textarea');
        this.promptField.rows = 4;
        this.promptField.placeholder = i18n.t('What should the AI write?');

        this.errorLine = document.createElement('div');
        this.errorLine.className = 'text-generation-error';

        this.submitButton = document.createElement('button');
        this.submitButton.type = 'button';
        this.submitButton.className = 'el-button el-button--primary el-button--small';
        this.submitButton.textContent = i18n.t('Create text');
        this.submitButton.addEventListener('click', () => this.generate());

        const footer = document.createElement('div');
        footer.className = 'text-generation-footer';
        footer.appendChild(this.submitButton);

        this.panel.appendChild(this.promptField);
        this.panel.appendChild(this.errorLine);
        this.panel.appendChild(footer);
        this.panel.addEventListener('keydown', (event) => this.onKeyDown(event));

        document.body.appendChild(this.panel);
        this.position();
        this.promptField.focus();
    }

    /** Fixed on document.body in viewport coordinates, like the placeholder dropdown. */
    position() {
        const bounds = this.quill.getBounds(this.range.index);
        const editorRect = this.quill.root.getBoundingClientRect();
        const panelRect = this.panel.getBoundingClientRect();

        let left = editorRect.left + bounds.left;
        let top = editorRect.top + bounds.top + bounds.height + 5;

        if (left + panelRect.width > window.innerWidth - 10) {
            left = Math.max(10, window.innerWidth - panelRect.width - 10);
        }
        if (top + panelRect.height > window.innerHeight - 10) {
            top = Math.max(10, editorRect.top + bounds.top - panelRect.height - 5);
        }

        this.panel.style.left = `${left}px`;
        this.panel.style.top = `${top}px`;
    }

    onKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.close();
            this.quill.setSelection(this.range.index, this.range.length);
            return;
        }

        // Enter submits, like the other Quill tool inputs; Shift+Enter is a new line.
        // On the focused button Enter is its native click.
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.target === this.promptField) {
            event.preventDefault();
            this.generate();
            return;
        }

        // Focus trap: the panel has two stops, the prompt and the button.
        if (event.key === 'Tab') {
            event.preventDefault();
            const next = document.activeElement === this.promptField ? this.submitButton : this.promptField;
            next.focus();
        }
    }

    onMouseDown(event) {
        if (this.panel && !this.panel.contains(event.target)) {
            this.close();
        }
    }

    async generate() {
        if (this.generating) return;

        const prompt = _.trim(this.promptField.value);
        if (!prompt) {
            this.errorLine.textContent = i18n.t('Please describe what the AI should write.');
            this.promptField.focus();
            return;
        }

        this.errorLine.textContent = '';
        this.generating = true;
        this.panel.classList.add('is-generating');
        this.panel.appendChild(this.loadingMask());

        // A panel closed while the request runs must not insert into the editor.
        const panel = this.panel;

        try {
            const result = await TextGenerationApi.generateText(
                _.get(useSetupStore(pinia), 'activeCms.id'),
                prompt,
                this.formats(),
                this.context()
            );

            // A request that could not be met inserts nothing: the panel keeps the prompt and says why.
            if (this.panel === panel && result.not_written) {
                this.errorLine.textContent = _.trim(
                    `${i18n.t('No text was written: it would need facts that no source holds.')} ${result.not_written_reason || ''}`
                );
                this.promptField.focus();
                return;
            }

            if (this.panel === panel) {
                this.insert(result.html);
                this.close();
                showDisclaimer();

                // Written while the knowledge base could not be searched: the text may lack the site's own facts.
                if (result.knowledge_base_unavailable) {
                    Notification.warning({
                        title: i18n.t('Knowledge base not available'),
                        message: i18n.t('The text was written without the knowledge base and may be incomplete.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                }
            }
        } catch (error) {
            Notification.error({
                title: i18n.t('The text could not be created.'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        } finally {
            this.generating = false;
            if (this.panel === panel) {
                this.panel.classList.remove('is-generating');
                this.panel.querySelector('.el-loading-mask').remove();
            }
        }
    }

    /**
     * Element's own v-loading markup: the panel is plain DOM, so it cannot use
     * the directive, but it waits the way every other admin area does.
     */
    loadingMask() {
        const mask = document.createElement('div');
        mask.className = 'el-loading-mask';
        mask.innerHTML = '<div class="el-loading-spinner"><svg viewBox="25 25 50 50" class="circular"><circle cx="50" cy="50" r="20" fill="none" class="path"></circle></svg><p class="el-loading-text"></p></div>';
        mask.querySelector('.el-loading-text').textContent = i18n.t('Creating text');

        return mask;
    }

    /** Replaces the selection, or inserts at the caret, as real editor formats. */
    insert(html) {
        let { index } = this.range;
        let { length } = this.range;
        const replacesSelection = length > 0;

        // A whole paragraph selected (triple click) ends in its line break; the
        // break stays, or the new text runs into the next paragraph.
        if (length > 0 && this.quill.getText(index + length - 1, 1) === '\n') {
            length -= 1;
        }

        // The model is never offered alignment (layout, not text), so the pasted
        // lines carry none; they take the alignment of the line they land in.
        const { align } = this.quill.getFormat(index);

        if (length > 0) {
            this.quill.deleteText(index, length, Quill.sources.USER);
        }

        // Inserted at the caret, the text gets its own lines: pasted HTML would
        // continue the caret's line with its first paragraph. A replaced
        // selection stays inline, so replacing a word keeps the sentence.
        const [, offsetInLine] = this.quill.getLine(index);
        const splitsLine = !replacesSelection && offsetInLine > 0;
        if (splitsLine) {
            this.quill.insertText(index, '\n', Quill.sources.USER);
            index += 1;
        }

        const lengthBefore = this.quill.getLength();
        this.quill.clipboard.dangerouslyPasteHTML(index, html, Quill.sources.USER);
        let insertedLength = this.quill.getLength() - lengthBefore;
        const endOfText = index + insertedLength;

        // ...and the text after the caret does not run into its last paragraph.
        // A list or heading ends in its own line break, a paragraph does not.
        const endsInLineBreak = this.quill.getText(endOfText - 1, 1) === '\n';
        const followedByLineBreak = this.quill.getText(endOfText, 1) === '\n';
        if (!replacesSelection && !endsInLineBreak && !followedByLineBreak) {
            this.quill.insertText(endOfText, '\n', Quill.sources.USER);
            insertedLength += 1;
        } else if (splitsLine && endsInLineBreak && followedByLineBreak) {
            // the empty line the split left behind
            this.quill.deleteText(endOfText, 1, Quill.sources.USER);
        }

        if (align) {
            this.quill.formatLine(index, insertedLength, { align }, Quill.sources.USER);
        }
        this.quill.setSelection(endsInLineBreak ? endOfText - 1 : endOfText, 0, Quill.sources.SILENT);
        this.quill.focus();
    }

    close() {
        if (this.panel) {
            this.panel.remove();
            this.panel = null;
        }
    }

    destroy() {
        document.removeEventListener('mousedown', this.boundOnMouseDown);
        this.close();
    }
}

/**
 * The host's side of the context: a copy of `config` whose text generation
 * names the record the editor belongs to. Only the `textGeneration` branch is
 * copied, so a context never leaks into other editors sharing `config`, while
 * everything else (link, placeholder) stays shared as before.
 *
 * `context` returns `{ type, id, title }`; `type` is an EX_AI_ContextType.
 */
export const withTextGenerationContext = (config, context) => ({
    ...config,
    modules: {
        ...config.modules,
        textGeneration: { ...config.modules.textGeneration, context },
    },
});

export default TextGenerationModule;
